/** One pen-down-to-pen-up path, in CSS pixels of the pad it was drawn on. */
export type SignatureStroke = { x: number; y: number }[];

export const INK_WIDTH_PX = 1.75;

/** Sizes the backing store to the element at the current DPR (this also wipes it) and returns a CSS-pixel context. */
export function prepareCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D | null {
  const ratio = window.devicePixelRatio || 1;
  canvas.width = Math.round(canvas.clientWidth * ratio);
  canvas.height = Math.round(canvas.clientHeight * ratio);
  const ctx = canvas.getContext("2d");
  ctx?.setTransform(ratio, 0, 0, ratio, 0, 0);
  return ctx;
}

/** Draws one stroke with midpoint quadratic curves so lines stay smooth on fast finger moves. */
export function drawStroke(ctx: CanvasRenderingContext2D, points: SignatureStroke, lineWidth = INK_WIDTH_PX) {
  if (!points.length) return;
  ctx.strokeStyle = "#000";
  ctx.fillStyle = "#000";
  ctx.lineWidth = lineWidth;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  if (points.length === 1) {
    ctx.beginPath();
    ctx.arc(points[0].x, points[0].y, lineWidth / 2, 0, Math.PI * 2);
    ctx.fill();
    return;
  }
  ctx.beginPath();
  ctx.moveTo(points[0].x, points[0].y);
  for (let i = 1; i < points.length - 1; i++) {
    const midX = (points[i].x + points[i + 1].x) / 2;
    const midY = (points[i].y + points[i + 1].y) / 2;
    ctx.quadraticCurveTo(points[i].x, points[i].y, midX, midY);
  }
  const last = points[points.length - 1];
  ctx.lineTo(last.x, last.y);
  ctx.stroke();
}

/**
 * Scale/offset that trims a signature to its ink and fits it inside a box (never enlarged), left-aligned and
 * vertically centred, so a signature drawn on a big pad reads naturally on a small printed line.
 */
export function fitStrokes(strokes: SignatureStroke[], width: number, height: number, padding = 2) {
  const points = strokes.flat();
  if (!points.length) return { scale: 1, dx: 0, dy: 0 };
  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const inkWidth = Math.max(Math.max(...xs) - minX, 1);
  const inkHeight = Math.max(Math.max(...ys) - minY, 1);
  const scale = Math.min((width - 2 * padding) / inkWidth, (height - 2 * padding) / inkHeight, 1);
  return {
    scale,
    dx: padding - minX * scale,
    dy: (height - inkHeight * scale) / 2 - minY * scale,
  };
}
